import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { Variant } from '../Status/PaymentFlow.types';
import { DrawerItems } from '@/components/Drawer/Drawer.styles';
import { Drawer } from '@/components/Drawer/Drawer';
import { Button } from '@/components/UI/Button/Button';
import { PaymentAgreementText } from '@/components/Payment/PaymentAgreementText';
import { PaymentMethods } from '@/components/Payment/PaymentMethods';
import * as S from '@/components/Ad/Promotions/PromotionDrawer.styles';
import { AdKind, MutationError } from '@/api/types/common.types';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import {
  formatDate,
  formatNumber,
  getLoginUrlWithReturn,
  sleep,
} from '@/utils/helpers';
import {
  CreatePaymentResponse,
  PaymentMethod,
  PaymentStatus,
  TerminalPaymentTargetType,
} from '@/api/types/payments.types';
import { Popup } from '@/components/UI/Popup/Popup';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import {
  buildDataAndOpenWebViewPaymentModal,
  buildServiceId,
  getPayButtonDataStatAttrValue,
  getPromotionsData,
  getUrl,
} from '@/components/Ad/Promotions/PromotionDrawer.helpers';
import { AddBalance } from '@/components/Payment/AddBalance';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { PaymentInTerminals } from '@/components/Payment/PaymentInTerminals';
import { AdBundle } from '@/api/types/categories.types';
import { AdService } from '@/api/types/services.types';
import { useAuthStore } from '@/store/useAuthStore';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { buildHashStringForTerminal } from '@/components/Payment/PaymentInTerminals.helpers';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { ServiceCheckboxes } from '@/components/Ad/Promotions/ServiceCheckboxes';
import { useTerminalPayment } from '@/components/Payment/useTerminalPayment';
import { usePostMessage } from '@/hooks/usePostMessage';
import { useCreatePayment } from '@/hooks/useCreatePayment';
import { useNewWindow } from '@/hooks/useNewWindow';
import { InfoDrawer } from '@/components/Ad/Promotions/InfoDrawer';
import { useWebViewPostMessage } from '@/bridge/hooks/useWebViewPostMessage';
import { EventName, Request } from '@/bridge/types';
import { useBridgeAPI, useBridgeState } from '@/bridge/hooks';
import { useWebViewPaymentModalStore } from '@/store/useWebViewPaymentModalStore';
import { getRefetchDelay } from '@/api/helpers/payments.helpers';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { Portal } from '@/components/UI/Portal/Portal';

interface PromotionDrawerProps {
  isOpen: boolean;
  toggle: (v?: boolean | React.MouseEvent<HTMLElement>) => void;
  type: AdKind | null;
  headerCloseIcon?: React.ReactNode;
  onSuccessPayment?: () => void;
  dataTestId?: string;
  portalSuccessModal?: boolean;
  minBundlePrice?: number;
  selectedVariant?: Variant;
}

export const PromotionDrawer: React.FC<PromotionDrawerProps> = ({
  isOpen,
  toggle,
  type,
  headerCloseIcon,
  onSuccessPayment,
  dataTestId,
  minBundlePrice,
  selectedVariant,
  portalSuccessModal = false,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const customPush = useCustomPush();
  const source = usePlatformSource();
  const [isNotEnoughFundsOpen, toggleNotEnoughFunds] = useToggleOpenState();
  const [isAddBalanceOpen, toggleAdBalance] = useToggleOpenState();
  const [isSuccessOpen, toggleSuccess] = useToggleOpenState();
  const [service, setService] = useState<AdService | null>(null);
  const [bundle, setBundle] = useState<AdBundle | null>(null);
  const [payment, setPayment] = useState(PaymentMethod.My_card);
  const openWebViewPaymentModal = useWebViewPaymentModalStore(
    state => state.open
  );
  const [cardId, setCardId] = useState<string | undefined>(undefined);
  const {
    newWindow: paymentWindow,
    close: closePaymentWindow,
    open: openPaymentWindow,
  } = useNewWindow();
  const [createPaymentError, setCreatePaymentError] = useState('');
  const {
    createPayment,
    loading: isPaymentLoading,
    paymentProvider,
  } = useCreatePayment({
    onSuccess: onCreatePaymentSuccess,
    onError: onCreatePaymentError,
    onRedirect: onCreatePaymentRedirect,
  });
  const [isTooManyRequestsOpen, toggleTooManyRequests] = useToggleOpenState();

  const {
    isTerminalOpen,
    toggleTerminal,
    isTerminal,
    createTerminalPayment,
    isCreateTerminalPaymentLoading,
  } = useTerminalPayment({ payment });

  const { categoryDetails } = useCurrentCategoryDetails();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const isBundles = type === null;

  const { adDetails, refetch: refetchAdDetails } = useAdDetails();
  const { data: userPaymentInfo, refetch: refetchUserPaymentInfo } =
    useQuery<GetUserResponse>(GET_USER, { errorPolicy: 'all' });

  const { isWebView } = useBridgeState();
  const bridgeAPI = useBridgeAPI();

  const walletDiscount = userPaymentInfo?.currentUser?.walletDiscount ?? 0;
  const isWallet = payment === PaymentMethod.Wallet;

  const payedAddingData = {
    title: t('pages.ad.paid_placement'),
    subtitle: t('pages.ad.paid_placement_subtitle'),
  };

  const drawerData = type ? getPromotionsData({ t })[type] : payedAddingData;
  const successPopupData =
    type &&
    getPromotionsData({
      t,
      isSuccessPopup: true,
      ...(adDetails?.vippedUntil && {
        vippedUntil: formatDate(adDetails.vippedUntil),
      }),
      ...(adDetails?.featuredUntil && {
        featuredUntil: formatDate(adDetails.featuredUntil),
      }),
    })[type];

  const services = {
    [AdKind.Bumped]: adDetails?.bumpServices,
    [AdKind.Premium]: adDetails?.featuredServices,
    [AdKind.Vipped]: adDetails?.vipServices,
  };

  function handlePaymentSuccess() {
    onSuccessPayment?.();
    toggle(false);
    if (type && service) {
      toggleSuccess();
    }
    refetchAdDetails();
    if (payment === PaymentMethod.Wallet) {
      refetchUserPaymentInfo();
    }
  }

  function onCreatePaymentError(
    status: PaymentStatus,
    errors?: MutationError[]
  ) {
    closePaymentWindow();

    const notEnoughFunds = errors?.some(el => el.code === 'insufficient_funds');
    if (notEnoughFunds) {
      return toggleNotEnoughFunds(true);
    }

    const tooManyRequests = errors?.some(el => el.code === '429');
    if (tooManyRequests) {
      toggleTooManyRequests();
      return;
    }

    if (errors?.length && status === PaymentStatus.Failure) {
      setCreatePaymentError(errors[0].message);
    }
  }

  function onCreatePaymentRedirect(redirectUrl: string) {
    if (isWebView) {
      bridgeAPI.openPayment(redirectUrl);
      return;
    }

    if (paymentWindow) {
      paymentWindow.location.href = redirectUrl;
    }
  }

  function onCreatePaymentSuccess(data: CreatePaymentResponse) {
    const url = getUrl({
      isBundles,
      bundle,
      service,
      type,
      data,
      adDetails,
      categoryDetails,
      walletDiscount,
      isWallet,
      payment,
    });

    if (isWebView) {
      handlePaymentSuccess();
      buildDataAndOpenWebViewPaymentModal({
        t,
        url,
        data,
        service,
        type,
        bundle,
        isBundles,
        isWallet,
        openWebViewPaymentModal,
      });
      return;
    }

    handlePaymentSuccess();

    if (url && paymentWindow) {
      paymentWindow.location.href = url;
      return;
    }

    closePaymentWindow();
  }

  function payWithTerminal() {
    if (!adDetails) return;

    createTerminalPayment({
      variables: {
        serviceId: buildServiceId(service, type, bundle, isBundles),
        targetId: adDetails.id,
      },
    });
  }

  function payWithNonTerminalMethods() {
    if (!adDetails) return;
    const walletBalance = userPaymentInfo?.currentUser?.walletBalance;
    createPayment({
      variables: {
        serviceId: buildServiceId(service, type, bundle, isBundles),
        targetId: adDetails.id,
        paymentMethod: payment,
        source,
        ...(payment === PaymentMethod.My_card && cardId && { cardId }),
      },
    });
    if (payment === PaymentMethod.Wallet && !walletBalance) {
      return;
    }

    if (!isWebView) openPaymentWindow();
  }

  function onPayClick() {
    if (!isAuthenticated && payment === PaymentMethod.Terminal) {
      customPush(
        getLoginUrlWithReturn(
          `${router.asPath}#${buildHashStringForTerminal(
            service,
            type,
            TerminalPaymentTargetType.Ad
          )}`,
          router.asPath
        )
      );
      return;
    }

    if (isTerminal) {
      payWithTerminal();
    } else {
      payWithNonTerminalMethods();
    }
  }

  function clearCreatePaymentError() {
    setCreatePaymentError('');
  }

  function startAdBalance() {
    toggleNotEnoughFunds(false);
    toggleAdBalance(true);
  }

  useEffect(() => {
    if (userPaymentInfo && !userPaymentInfo.currentUser?.cards?.nodes.length) {
      setPayment(PaymentMethod.Card);
    }
  }, [userPaymentInfo]);

  useEffect(() => {
    if (type) {
      const currentService = services[type];

      if (!currentService) return;

      if (currentService.length > 2) {
        const secondToLastItem = currentService[currentService.length - 2];
        setService(secondToLastItem);
      } else {
        setService(currentService[1] ?? currentService[0]);
      }
    }

    if (isBundles && categoryDetails?.bundles.length) {
      const { bundles } = categoryDetails;

      setService(null);
      setBundle(bundles[1] ?? bundles[0]);
    }
  }, [type, isBundles, categoryDetails?.bundles]);

  function handleWebViewPostMessage(
    params: Request.Params[EventName.PaymentStatus]
  ) {
    if (!isOpen || isAddBalanceOpen) return;

    const delay = getRefetchDelay(paymentProvider);

    openWebViewPaymentModal(params);

    if (params.status === PaymentStatus.Success) {
      toggle(false);

      sleep(delay).then(() => {
        refetchAdDetails();
        if (payment === PaymentMethod.Wallet) {
          refetchUserPaymentInfo();
        }
        if (type && service) {
          toggleSuccess();
        }
        onSuccessPayment?.();
      });
    }
  }

  function handlePostMessage() {
    if (!isOpen || isAddBalanceOpen) return;

    const delay = getRefetchDelay(paymentProvider);

    sleep(delay).then(handlePaymentSuccess);
  }

  useWebViewPostMessage<Request.Params[EventName.PaymentStatus]>({
    eventName: 'paymentStatus',
    handler: handleWebViewPostMessage,
  });

  usePostMessage({
    postMessageName: 'paymentFinished',
    handler: handlePostMessage,
  });

  function handleSuccessClose() {
    toggleSuccess();
  }

  function handleCloseTooManyRequestsDrawer() {
    toggleTooManyRequests(false);
    toggle(false);
  }

  if (!drawerData || !adDetails || !userPaymentInfo) {
    return null;
  }

  const drawerTitle =
    'iconName' in drawerData
      ? {
          iconSrc: `/images/${drawerData.iconName}.svg`,
          text: drawerData.title,
        }
      : drawerData.title;
  const isSingleBundle = (categoryDetails?.bundles.length || 0) < 2;
  const isBundleVariant = selectedVariant?.type === 'bundle';

  const payButtonTitle = isTerminal
    ? t('common.terminal_top_up')
    : minBundlePrice && isBundleVariant
    ? t('common.pay_with_price', {
        price: formatNumber(bundle?.price, 2),
      })
    : t('common.pay');

  const notEnoughFundsActions = {
    left: {
      isGray: true,
      text: t('common.close'),
      onClick: toggleNotEnoughFunds,
    },
    right: { text: t('common.top_up'), onClick: startAdBalance },
  };

  const renderSuccessModal = () => {
    return (
      <Popup
        dataTestId={AdPageTestIds.popup.success}
        isOpen={isSuccessOpen}
        close={handleSuccessClose}
        title={successPopupData?.title}
        capitalizeTitle
        description={successPopupData?.subtitle}
        icon={`${successPopupData?.iconName}.svg`}
        actions={{
          left: { text: t('common.ok'), onClick: handleSuccessClose },
        }}
      />
    );
  };

  return (
    <>
      <Drawer
        isHeadFixed={false}
        dataTestId={dataTestId ?? AdPageTestIds.promotionDrawer.drawer}
        hasBottomMargin={false}
        isOpen={isOpen}
        close={toggle}
        title={drawerTitle}
        subtitle={drawerData.subtitle}
        subtitleVariant="4"
        {...(headerCloseIcon && {
          left: { icon: headerCloseIcon, onClick: toggle },
        })}
        bottom={
          <Button
            data-testid={AdPageTestIds.promotionDrawer.payButton}
            appearance="secondary"
            isFullWidth
            onClick={onPayClick}
            disabled={isPaymentLoading || isCreateTerminalPaymentLoading}
            data-stat={getPayButtonDataStatAttrValue(payment, type)}
          >
            {payButtonTitle}
          </Button>
        }
        className="action"
      >
        {(isBundles && isSingleBundle) || (
          <DrawerItems data-testid={AdPageTestIds.promotionDrawer.services}>
            <S.Title $variant="3">
              {t(`pages.ad.${isBundles ? 'type' : 'term'}_of_service`)}
            </S.Title>
            <ServiceCheckboxes
              services={services}
              type={type}
              bundle={bundle}
              walletDiscount={walletDiscount}
              isWallet={isWallet}
              isBundles={isBundles}
              setBundle={setBundle}
              setService={setService}
              service={service}
            />
          </DrawerItems>
        )}
        <PaymentMethods
          payment={payment}
          setPayment={setPayment}
          setCardId={setCardId}
          isPromotionDrawerOpen={isOpen}
        />
        <PaymentAgreementText />
      </Drawer>

      <Popup
        dataTestId={AdPageTestIds.popup.notEnoughFunds}
        isOpen={isNotEnoughFundsOpen}
        close={toggleNotEnoughFunds}
        title={t('common.attention')}
        description={t('pages.ad.not_enough_money_top_up_your_balance')}
        actions={notEnoughFundsActions}
      />

      {portalSuccessModal ? (
        <Portal>{renderSuccessModal()}</Portal>
      ) : (
        renderSuccessModal()
      )}

      <InfoDrawer
        isOpen={isTooManyRequestsOpen}
        onClose={handleCloseTooManyRequestsDrawer}
        description={t('pages.ad.too_many_requests')}
      />

      <InfoDrawer
        isOpen={!!createPaymentError}
        onClose={clearCreatePaymentError}
        description={createPaymentError}
      />

      <AddBalance
        isOpen={isAddBalanceOpen}
        toggle={toggleAdBalance}
        service={service}
        bundle={bundle}
      />

      <PaymentInTerminals
        dataTestId={AdPageTestIds.promotionDrawer.terminalPaymentDrawer}
        isOpen={isTerminalOpen}
        toggle={toggleTerminal}
        service={service}
        serviceType={type}
        bundle={bundle}
      />
    </>
  );
};
